import {
  Agent_EnabledAsHelper,
  Agent_HelperPermissions,
  Agent_LicenseFilter,
  Agent_LogLevel,
  Agent_Os_VersionFilter,
  Agent_QuickFilter,
  Agent_SortField,
  Agent_SortOrder,
  Agent_Status,
  Agent_WhereInput,
} from '@/graphql/codegen/generated/graphql';

export const agentsFilters = {
  setLicenseFilter(filtersModel: any): Agent_WhereInput['licenseFilter'] {
    const licenseModel = filtersModel?.licensesFilter;
    const required: Record<
      Exclude<Agent_LicenseFilter, Agent_LicenseFilter.WithoutBuildCache | Agent_LicenseFilter.WithBuildCache | Agent_LicenseFilter.FixedLicense | Agent_LicenseFilter.FloatingLicense>,
      any
    > = {
      [Agent_LicenseFilter.CloudHelper]: licenseModel.cloudAgent,
      [Agent_LicenseFilter.NoLicense]: licenseModel.local,
      [Agent_LicenseFilter.OnlyInitiator]: licenseModel.initiator,
      [Agent_LicenseFilter.OnlyCiInitiator]: licenseModel.ciInitiator,
      [Agent_LicenseFilter.OnPremHelper]: licenseModel.helper,
      [Agent_LicenseFilter.HelperAndInitiator]: licenseModel.bothHelperAndInitiator,
      [Agent_LicenseFilter.HelperAndCiInitiator]: licenseModel.bothHelperAndCIInitiator,
    };
    const additional: Record<
      Extract<Agent_LicenseFilter, Agent_LicenseFilter.FixedLicense | Agent_LicenseFilter.FloatingLicense | Agent_LicenseFilter.WithBuildCache | Agent_LicenseFilter.WithoutBuildCache>,
      boolean
    > = {
      [Agent_LicenseFilter.FixedLicense]: licenseModel?.helperType === 'Fixed',
      [Agent_LicenseFilter.FloatingLicense]: licenseModel?.helperType === 'Floating',
      [Agent_LicenseFilter.WithBuildCache]: licenseModel?.initiatorType?.withBuildCache === 'With Build Cache' || licenseModel?.ciInitiatorType?.withBuildCache === 'With Build Cache',
      [Agent_LicenseFilter.WithoutBuildCache]: licenseModel?.initiatorType?.withBuildCache === 'Without Build Cache' || licenseModel?.ciInitiatorType?.withBuildCache === 'Without Build Cache',
    };

    return [...(Object.keys(required) as Array<keyof typeof required>).filter(key => !!required[key]), ...(Object.keys(additional) as Array<keyof typeof additional>).filter(key => !!additional[key])];
  },

  setStatusFilter(filtersModel: any): Agent_WhereInput['statusFilter'] {
    const statusModel = filtersModel.status;
    const map = {
      helping: Agent_Status.Helping,
      initiating: Agent_Status.Initiating,
      offline: Agent_Status.Offline,
      ready: Agent_Status.Ready,
      updating: Agent_Status.Updating,
      stopped: Agent_Status.Stopped,
      expired: Agent_Status.Expired,
    };
    return (Object.keys(statusModel) as Array<keyof typeof map>).filter(key => statusModel[key]).map(key => map[key]);
  },

  setBuildGroupFilter(filtersModel: any): Agent_WhereInput['buildGroupsFilter'] {
    const buildGroupsModel = filtersModel.buildGroups;
    return Object.keys(buildGroupsModel).filter(key => buildGroupsModel[key]);
  },

  setOsVersionFilter(filtersModel: any): Agent_WhereInput['osVersionFilter'] {
    const osVersionModel = filtersModel.osVersionFilter;
    const map = {
      linux: Agent_Os_VersionFilter.Linux,
      windows: Agent_Os_VersionFilter.Windows,
    };
    return (Object.keys(osVersionModel) as Array<keyof typeof map>).filter(key => osVersionModel[key]).map(key => map[key]);
  },

  setAgentsQuickFilter(filtersModel: 'showAll' | 'active' | 'inProcess' | 'incompatibleVersion' | 'unlicensedAgents'): Agent_WhereInput['quickFilter'] {
    const map: Record<typeof filtersModel, Agent_QuickFilter> = {
      showAll: Agent_QuickFilter.All,
      active: Agent_QuickFilter.Online,
      inProcess: Agent_QuickFilter.InProcess,
      incompatibleVersion: Agent_QuickFilter.IncompatibleVersion,
      unlicensedAgents: Agent_QuickFilter.UnlicensedAgents,
    };
    return map[filtersModel as keyof typeof map];
  },

  setAgentsSortBy(sortValue: any): NonNullable<Agent_WhereInput['sort']> {
    const map = {
      name: Agent_SortField.Name,
      status: Agent_SortField.Status,
      alerts: Agent_SortField.Alerts,
      cpu: Agent_SortField.AvailableCpu,
      cache: Agent_SortField.UsedHelperCache,
      LastConnected: Agent_SortField.LastConnected,
    };
    const order = sortValue?.descending ? Agent_SortOrder.Descending : Agent_SortOrder.Ascending;
    return {
      field: map[sortValue?.sortBy as keyof typeof map],
      order,
    };
  },

  setLogLevelFilter(filtersModel: any): Agent_WhereInput['logLevelFilter'] {
    const logLevelModel = filtersModel.logLevel;
    const map = {
      Basic: Agent_LogLevel.Basic,
      Detailed: Agent_LogLevel.Detailed,
      Extended: Agent_LogLevel.Extended,
      Intermediate: Agent_LogLevel.Intermediate,
      Minimal: Agent_LogLevel.Minimal,
    };
    return (Object.keys(logLevelModel) as Array<keyof typeof map>).filter(key => logLevelModel[key]).map(key => map[key]);
  },

  setCacheSizeFilter(filtersModel: any): Agent_WhereInput['cacheSizeFilter'] {
    const { min, max } = filtersModel.cacheView;
    return {
      from: min,
      to: max,
    };
  },

  setEnabledAsHelperFilter(filtersModel: any): Agent_WhereInput['enabledAsHelperFilter'] {
    const enabledAsHelperModel = filtersModel.helpers;
    const map = {
      enabled: Agent_EnabledAsHelper.Enabled,
      disabled: Agent_EnabledAsHelper.Disabled,
    };
    return (Object.keys(enabledAsHelperModel) as Array<keyof typeof map>).filter(key => enabledAsHelperModel[key]).map(key => map[key]);
  },

  setHelperPermissionsFilter(filtersModel: any): Agent_WhereInput['helperPermissionsFilter'] {
    const helperPermissionsModel = filtersModel.agentHelperPermissions;
    const map = {
      allowed: Agent_HelperPermissions.AllowedToEnableDisable,
      notAllowed: Agent_HelperPermissions.NotAllowedToEnableDisable,
    };
    return (Object.keys(helperPermissionsModel) as Array<keyof typeof map>).filter(key => helperPermissionsModel[key]).map(key => map[key]);
  },

  setAvailableCpuFilter(filtersModel: any): Agent_WhereInput['availableCpuFilter'] {
    const { min, max } = filtersModel.cpuView;
    return {
      from: min,
      to: max,
    };
  },

  setUsedCpuFilter(filtersModel: any): Agent_WhereInput['usedCpuFilter'] {
    const { min, max } = filtersModel.cpuView;
    return {
      from: min,
      to: max,
    };
  },

  setCoresFilter(filtersModel: any): Agent_WhereInput['coresFilter'] {
    const { coreView }: { coreView: { enabledFilters: number[] } } = filtersModel;
    return coreView.enabledFilters;
  },
};
